import type { AccountDetails, TaxCode } from "@/apis/gql/types";
import { CurrencyInputCell } from "@/components/shared/cells/currencey-input";
import { CurrencyCell } from "@/components/shared/cells/currency";
import { MultiLineTextCell } from "@/components/shared/cells/multi-line-text-input-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { SelectInputCell } from "@/components/shared/cells/select-input";
import { TextInputCell } from "@/components/shared/cells/text-input-cell";
import { AccountsDropdown } from "@/components/shared/select-inputs/accounts-dropdown";
import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";
import type { ColumnDef } from "@tanstack/react-table";
import { useAccountsDataContext } from "./accounts-data-provider";
import { AccountNoInputCell } from "./cells/account-no-cell";
import { SpecialRulesCell } from "./cells/special-rules-cell";
import { AccountNo } from "./headers/accountNo";
import { AccountTypeHeader } from "./headers/accountType";
import { DescriptionHeader } from "./headers/description";
import { MaxHeader } from "./headers/max";
import { MinHeader } from "./headers/min";
import { NameHeader } from "./headers/name";
import { SelectHeader } from "./headers/select";
import { SpecialRulesHeader } from "./headers/specialRules";

const selectColumn: ColumnDef<any> = {
  id: "select",
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => <SelectCell {...cell} />,
  enableSorting: false,
  enableHiding: false,
  size: 40,
  minSize: 40,
};

const accountNoColumn: () => ColumnDef<any> = () => ({
  accessorKey: "accountNo",
  id: "accountNo",
  size: 450,
  minSize: 300,
  enableResizing: false,
  header: (header) => <AccountNo {...header} />,
  sortingFn: (a, b) => {
    const aNum = Number.parseInt(a.original.accountNo?.replace(/\D/g, ""));
    const bNum = Number.parseInt(b.original.accountNo?.replace(/\D/g, ""));
    return aNum - bNum;
  },

  cell: (cell) => {
    const { updateAccount } = useAccountsDataContext();
    const value = cell.row.original.accountNo;
    const accountId = cell.row.original.accountId;
    const automatic = cell.row.original.automatic;
    const accountType = cell.row.original.accountType;

    const prefix = automatic ? (accountType === "REVENUE" ? "AM" : "AV") : "";

    const saveHandler = async (value: string) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            accountNo: value,
          },
        },
      });
    };
    return (
      <AccountNoInputCell
        key={accountId}
        value={value}
        prefix={prefix}
        saveHandler={saveHandler}
        className="h-[20px]"
      />
    );
  },
});

const nameColumn: () => ColumnDef<any> = () => ({
  accessorKey: "accountName",
  id: "accountName",
  size: 900,
  minSize: 900,
  enableResizing: false,
  header: (header) => <NameHeader {...header} />,
  sortingFn: (a, b) =>
    a.original.accountName.localeCompare(b.original.accountName),

  cell: (cell) => {
    const { accounts, updateAccount } = useAccountsDataContext();
    const value = cell.row.original.accountName;

    const accountId = cell.row.original.accountId;
    const saveHandler = async (value: string) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            accountName: value,
          },
        },
      });
    };

    // Check if this account name is duplicated
    const trimmedName = value.trim().toLowerCase();
    const duplicateCount = accounts.filter(
      (account) => account.accountName.trim().toLowerCase() === trimmedName,
    ).length;
    const isDuplicate = duplicateCount > 1;

    return (
      <MultiLineTextCell
        key={accountId}
        value={value}
        saveHandler={saveHandler}
        classNameInput={`h-[20px] ml-0 pl-0 ${
          isDuplicate ? "border-yellow-400 border-2 bg-yellow-50" : ""
        }`}
      />
    );
  },
});

const descriptionColumn: () => ColumnDef<any> = () => ({
  accessorKey: "accountDescription",
  id: "accountDescription",
  size: 1500,
  minSize: 1500,
  enableResizing: false,
  header: (header) => <DescriptionHeader {...header} />,
  enableSorting: false,
  cell: (cell) => {
    const { accounts, taxCodes, updateAccount } = useAccountsDataContext();
    const accountId = cell.row.original.accountId;
    const value = cell.row.original.accountDescription;
    const automatic = cell.row.original.automatic;
    const accountType = cell.row.original.accountType;

    const descriptionSaveHandler = async (value: string) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            accountDescription: value,
          },
        },
      });
    };

    const parentAccountSaveHandler = async (value: string) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            parentAccountId: value,
          },
        },
      });
    };
    const selectableAccounts = accounts.filter((account) => !account.automatic);
    const selectedAccount = accounts.find(
      (account) => account.accountId === cell.row.original.parentAccountId,
    );

    const taxCodeId = cell.row.original?.taxCodeId;

    const taxCode = taxCodes.find((taxCode) => taxCode.taxCodeId === taxCodeId);
    const taxCodeName = taxCode ? taxCode.name : "Steuerschlüssel";
    const currentOption = {
      label: taxCodeName ?? "",
      alternativeLabels: [taxCode?.datevBu ?? ""],
      value: taxCode?.taxCodeId ?? "",
      subLabel: taxCode?.datevBu || "Nicht gesetzt",
    };
    const taxCodeOptions = taxCodes?.map(
      (taxCode): ComboOption => ({
        label: taxCode.name,
        alternativeLabels: [taxCode.datevBu],
        value: taxCode.taxCodeId,
        subLabel: taxCode.datevBu,
      }),
    );

    const taxCodeSaveHandler = async (value: string) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            taxCodeId: value,
          },
        },
      });
    };

    // For automatic revenue accounts (the majority of revenue accounts) it does not make sense to set a parent account or tax code
    return automatic && accountType !== "REVENUE" ? (
      <div className="flex space-x-4">
        <AccountsDropdown
          key={accountId}
          currentOption={{
            value: selectedAccount?.accountId ?? "",
            label: selectedAccount?.accountName ?? "Sachkonto",
            alternativeLabels: [],
            subLabel: selectedAccount?.accountNo ?? "Nicht gesetzt",
          }}
          fullWidth={false}
          onChange={parentAccountSaveHandler}
          items={selectableAccounts}
          className={`border-gray-300 focus:border-blue-500 ${
            !selectedAccount?.accountId
              ? "border-yellow-400 border-2 bg-yellow-50"
              : ""
          }`}
        />

        <ComboBox
          currentOption={currentOption}
          onChange={taxCodeSaveHandler}
          items={taxCodeOptions ?? []}
          placeholder="Steuerschlüssel"
          fullWidth={false}
          emptyLabel="Keine BU gefunden"
          className={
            !taxCodeId ? "border-yellow-400 border-2 bg-yellow-50" : ""
          }
        />
      </div>
    ) : (
      <MultiLineTextCell
        key={accountId}
        value={value}
        saveHandler={descriptionSaveHandler}
        classNameInput="h-[20px] "
      />
    );
  },
});

const accountType: () => ColumnDef<any> = () => ({
  accessorKey: "accountType",
  id: "accountType",
  size: 400,
  minSize: 400,
  enableResizing: false,
  header: (header) => <AccountTypeHeader {...header} />,
  enableSorting: false,
  cell: (cell) => {
    const { updateAccount } = useAccountsDataContext();
    const value = cell.row.original.accountType;
    const accountId = cell.row.original.accountId;

    const options = [
      { group: "Kontoart", value: "ASSET", label: "Anlage" },
      { group: "Kontoart", value: "EXPENSE", label: "Aufwand" },
      { group: "Kontoart", value: "GWG", label: "GWG" },
      { group: "Kontoart", value: "REVENUE", label: "Erlöse" },
    ];
    const saveHandler = async (value: string | null) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            accountType: value as any,
          },
        },
      });
    };
    return (
      <SelectInputCell
        value={value}
        options={options}
        key={accountId}
        placeholder="Kontoart"
        saveHandler={saveHandler}
      />
    );
  },
});

const minAmountColumn: () => ColumnDef<any> = () => ({
  accessorKey: "min",
  id: "min",
  size: 300,
  minSize: 300,
  enableResizing: false,
  header: (header) => <MinHeader {...header} />,
  enableSorting: false,
  cell: (cell) => {
    const { updateAccount } = useAccountsDataContext();
    const value = cell.row.original.min;
    const accountId = cell.row.original.accountId;
    const saveHandler = async (value: number | null) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            min: value,
          },
        },
      });
    };
    return (
      <CurrencyInputCell
        key={accountId}
        value={value}
        saveHandler={saveHandler}
        className="h-[20px]"
      />
    );
  },
});

const maxAmountColumn: () => ColumnDef<any> = () => ({
  accessorKey: "max",
  id: "max",
  size: 300,
  minSize: 300,
  enableResizing: false,
  header: (header) => <MaxHeader {...header} />,
  enableSorting: false,
  cell: (cell) => {
    const { updateAccount } = useAccountsDataContext();
    const value = cell.row.original.max;
    const accountId = cell.row.original.accountId;
    const saveHandler = async (value: number | null) => {
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            max: value,
          },
        },
      });
    };
    return (
      <CurrencyInputCell
        key={accountId}
        value={value}
        saveHandler={saveHandler}
        className="h-[20px]"
      />
    );
  },
});

const automaticColumn: () => ColumnDef<any> = () => ({
  accessorKey: "automatic",
  id: "automatic",
  size: 0,
  minSize: 0,
  enableResizing: false,
  enableHiding: true,
  enableSorting: false,
  enableColumnFilter: true,
  filterFn: "equals",
  cell: () => null, // Hidden column, only used for filtering
});

const specialRulesColumn: () => ColumnDef<any> = () => ({
  id: "specialRules",
  size: 800,
  minSize: 800,
  enableResizing: false,
  header: (header) => <SpecialRulesHeader {...header} />,
  enableSorting: false,

  cell: (cell) => {
    const { updateAccount } = useAccountsDataContext();
    const value = cell.row.original;
    const accountId = cell.row.original.accountId;
    const saveHandler = async (value: Partial<AccountDetails>) => {
      console.log(value);
      await updateAccount({
        variables: {
          accountId: cell.row.original.accountId,
          updatedFields: {
            ...value,
          },
        },
      });
    };
    return (
      <SpecialRulesCell
        key={accountId}
        value={value}
        saveHandler={saveHandler}
        className="h-[20px]"
      />
    );
  },
});

export const columns: () => ColumnDef<any>[] = () => [
  selectColumn,
  automaticColumn(),
  accountNoColumn(),
  nameColumn(),
  descriptionColumn(),
  minAmountColumn(),
  maxAmountColumn(),
  specialRulesColumn(),
  accountType(),
];
